import { Restore, Assignment, Close, Save, Add } from '@mui/icons-material';
import {
  Divider,
  Box,
  Button,
  Fade,
  Grid,
  MenuItem,
  Typography,
  TextField,
  FormControl
} from '@mui/material';
import { LoadingButton } from '@mui/lab';
import { deepEqual } from 'fast-equals';
import { FieldArray, Form, Formik } from 'formik';
import { useSnackbar } from 'notistack';
import { FC, Fragment, useEffect, useState } from 'react';
import * as Yup from 'yup';
import { formatInputPhoneNumber } from '../../../helpers';
import { Card, Loader, Modal, SMBSelect } from '../../../components';
import {
  vendorResetPassword,
  vendorResetTwoFactor,
  createVendorUserManagementVendorsUser,
  updateVendorUserManagementVendorsUser
} from '../../../fetch';
import {
  IDropdownResponse,
  IVendorDetails,
  IVendorDetailsPut,
  IUserType,
  userTypes,
  IEditableDropdown
} from '../../../models';

interface IAddEditVendorModal {
  open: boolean;
  onClose: (hasSubmitted?: boolean) => void;
  fetchVendorManagementUsers?: () => {};
  currentVendor?: IVendorDetails;
  modalTitle?: string;
  selectedUserManagementVendor?: string | number;
  userManagementVendorOptions?: IDropdownResponse[];
  editOptions?: IEditableDropdown[];
}

export const AddEditVendor: FC<IAddEditVendorModal> = ({
  open,
  onClose,
  fetchVendorManagementUsers,
  currentVendor,
  modalTitle,
  selectedUserManagementVendor,
  userManagementVendorOptions,
  editOptions
}) => {
  const { enqueueSnackbar } = useSnackbar();
  const [isResettingUser, setIsResettingUser] = useState<boolean>(false);
  const [isResettingAuth, setResettingAuth] = useState(false);
  // Set input fields to be disabled on initial load to prevent password managers from filling them out.
  const [isFormDisabled, setIsFormDisabled] = useState<boolean>(true);

  const handleResetAuth = async (original: any, showLoader?: boolean) => {
    const result = window.confirm('Are you sure you want to reset two factor auth for this user?');
    if (result) {
      try {
        await vendorResetTwoFactor(selectedUserManagementVendor, currentVendor?.personId);
        enqueueSnackbar('Two factor auth reset!', {
          variant: 'success'
        });
      } catch (error: any) {
        const variant =
          error?.Detail &&
          (error.Detail.includes('User not enrolled in MFA') ||
            error.Detail.includes('All user MFA enrollments inactive'))
            ? 'warning'
            : 'error';
        enqueueSnackbar(error?.Detail ?? 'User does not exist', {
          variant: variant
        });
      }
    }
  };

  // Disable input fields on load to prevent password managers from auto-filling
  useEffect(() => {
    if (!open) {
      setIsFormDisabled(true);
      return;
    }

    const timer = setTimeout(() => {
      setIsFormDisabled(false);
    }, 1000);

    return () => {
      clearTimeout(timer);
    };
  }, [open]);

  const AddEditVendorSchema = Yup.object().shape({
    firstName: Yup.string().required('Required'),
    lastName: Yup.string().required('Required'),
    email: Yup.string().required('Required'),
    phoneNumber: Yup.string().required('Required'),
    isActive: Yup.boolean().required('Required'),
    vendorUserRoles: Yup.array()
  });

  const isEmailActive = !currentVendor;

  const isDisabled = (predicate: boolean) => {
    if (!currentVendor) {
      return false;
    }
    return predicate;
  };
  return (
    <>
      <Formik
        enableReinitialize={true}
        initialValues={{
          firstName: currentVendor?.firstName ?? '',
          lastName: currentVendor?.lastName ?? '',
          email: currentVendor?.email ?? '',
          phoneNumber:
            currentVendor && currentVendor.phoneNumber
              ? formatInputPhoneNumber(currentVendor.phoneNumber)
              : '',
          isActive: currentVendor?.isActive ?? true,
          vendorUserRoles: currentVendor?.vendorUserInfo ?? []
        }}
        validationSchema={AddEditVendorSchema}
        onSubmit={async (values, actions) => {
          const data: IVendorDetailsPut = {
            ...values,
            vendorUserRoles: values.vendorUserRoles.map(role => {
              return {
                vendorId: role.vendorId,
                roles: role.roles
              };
            })
          };

          if (currentVendor) {
            try {
              await updateVendorUserManagementVendorsUser(currentVendor?.personId, data);
              enqueueSnackbar('User Updated!', {
                variant: 'success'
              });
              onClose(true);
              fetchVendorManagementUsers();
              actions.resetForm();
            } catch (error) {
              enqueueSnackbar(error?.Detail ?? 'User was not updated', {
                variant: 'error'
              });
            }
          } else {
            try {
              await createVendorUserManagementVendorsUser(data);
              enqueueSnackbar('User Created!', {
                variant: 'success'
              });
              onClose(true);
              fetchVendorManagementUsers();
              actions.resetForm();
            } catch (error) {
              enqueueSnackbar(error?.Detail ?? 'User was not created', {
                variant: 'error'
              });
            }
          }
        }}
      >
        {({
          resetForm,
          isSubmitting,
          values,
          initialValues,
          setFieldValue,
          errors,
          touched,
          handleSubmit,
          dirty,
          isValid,
          handleBlur,
          setValues
        }) => {
          const handleClose = () => {
            if (!deepEqual(initialValues, values)) {
              const result = window.confirm(
                'You have unsaved changes, are you sure you want to exit?'
              );
              if (result) {
                resetForm();
                onClose();
              } else {
                return;
              }
            } else {
              onClose();
            }
          };
          return (
            <Modal
              open={open}
              onClose={() => {
                handleClose();
              }}
              maxWidth="md"
            >
              {isSubmitting && <Loader type="overlay" position="centered" />}
              <Fade in={open}>
                <Box mt={1}>
                  <Form onSubmit={handleSubmit} autoComplete="none">
                    <Box marginBottom="0.5rem" display="flex" flexWrap="wrap">
                      <Typography sx={{ flex: 1 }} variant="h5">
                        {modalTitle || (currentVendor ? `Edit User` : 'Add User')}
                      </Typography>
                      <Box display="flex" alignItems="center" flexWrap="wrap">
                        {!!currentVendor && (
                          <LoadingButton
                            type="button"
                            startIcon={<Restore />}
                            disabled={isResettingUser}
                            loading={isResettingUser}
                            loadingPosition="start"
                            color="secondary"
                            onClick={async () => {
                              const result = window.confirm(
                                `Are you sure you want to reset this password?`
                              );
                              if (result) {
                                try {
                                  setIsResettingUser(true);
                                  await vendorResetPassword(
                                    selectedUserManagementVendor,
                                    currentVendor?.personId
                                  );
                                  enqueueSnackbar('User emailed reset password instructions!', {
                                    variant: 'success'
                                  });
                                } catch (error) {
                                  enqueueSnackbar(
                                    error?.Detail ??
                                      `Error resetting user password, please try again.`,
                                    {
                                      variant: 'error'
                                    }
                                  );
                                } finally {
                                  setIsResettingUser(false);
                                }
                              }
                            }}
                          >
                            Reset Password
                          </LoadingButton>
                        )}
                        {!!currentVendor && (
                          <LoadingButton
                            type="button"
                            startIcon={<Restore />}
                            loading={isResettingAuth}
                            loadingPosition="start"
                            color="secondary"
                            onClick={async () => {
                              setResettingAuth(true);
                              await handleResetAuth(currentVendor);
                              setResettingAuth(false);
                            }}
                          >
                            Reset Two Factor Auth
                          </LoadingButton>
                        )}
                      </Box>
                    </Box>
                    <Divider />
                    <Card title="Info" icon={<Assignment />}>
                      <Grid container spacing={1}>
                        <Grid item xs={12} sm={6}>
                          <TextField
                            fullWidth
                            required
                            variant="outlined"
                            autoComplete="nope"
                            label="First Name"
                            name="firstName"
                            value={values.firstName}
                            disabled={isFormDisabled}
                            onBlur={handleBlur}
                            size="small"
                            inputProps={{
                              'data-lpignore': 'true'
                            }}
                            error={touched.firstName && errors && errors.firstName ? true : false}
                            helperText={touched.firstName && errors && errors.firstName}
                            onChange={e => setFieldValue('firstName', e.target.value)}
                          />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                          <TextField
                            fullWidth
                            required
                            variant="outlined"
                            autoComplete="nope"
                            label="Last Name"
                            name="lastName"
                            inputProps={{
                              'data-lpignore': 'true'
                            }}
                            value={values.lastName}
                            disabled={isFormDisabled}
                            onBlur={handleBlur}
                            size="small"
                            error={touched.lastName && errors && errors.lastName ? true : false}
                            helperText={touched.lastName && errors && errors.lastName}
                            onChange={e => setFieldValue('lastName', e.target.value)}
                          />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                          <TextField
                            onChange={e => setFieldValue('email', e.target.value)}
                            error={touched.email && errors && errors.email ? true : false}
                            helperText={touched.email && errors && errors.email}
                            fullWidth={true}
                            id="email"
                            label="Email (Username)"
                            size="small"
                            name="email"
                            onBlur={handleBlur}
                            required={true}
                            autoComplete="off"
                            value={values.email}
                            disabled={isFormDisabled || (currentVendor && !isEmailActive)}
                            inputProps={{
                              'data-lpignore': true // Last Pass disable auto-fill
                            }}
                          />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                          <TextField
                            fullWidth
                            required
                            variant="outlined"
                            autoComplete="nope"
                            label="Phone Number"
                            name="phone"
                            value={values.phoneNumber}
                            disabled={isFormDisabled}
                            onBlur={handleBlur}
                            size="small"
                            onChange={e => {
                              setFieldValue('phoneNumber', formatInputPhoneNumber(e.target.value));
                            }}
                            error={Boolean(touched.phoneNumber && errors.phoneNumber)}
                            helperText={touched.phoneNumber && errors.phoneNumber}
                          />
                        </Grid>
                        <Grid item xs={12}>
                          <FieldArray name="vendorUserRoles">
                            {({ push, remove }) => {
                              const selectableUserVendors =
                                editOptions ?? userManagementVendorOptions;
                              return (
                                <>
                                  <Grid container spacing={1} alignItems="flex-start">
                                    {values?.vendorUserRoles?.map((userRole, index) => {
                                      return (
                                        <Fragment key={`${index}`}>
                                          <Grid item xs={12} sm={5}>
                                            <FormControl
                                              required
                                              fullWidth
                                              error={!userRole?.vendorId}
                                              variant="outlined"
                                              size="small"
                                            >
                                              <SMBSelect
                                                name={`vendors`}
                                                id={`vendors`}
                                                size="small"
                                                label="Vendor"
                                                variant="outlined"
                                                required
                                                value={userRole?.vendorId}
                                                error={!userRole?.vendorId}
                                                helperText={!userRole?.vendorId ? 'Required' : ''}
                                                handleClear={() =>
                                                  setFieldValue(
                                                    `vendorUserRoles.${index}.vendorId`,
                                                    ''
                                                  )
                                                }
                                                onBlur={handleBlur}
                                                disabled={isDisabled(
                                                  (
                                                    selectableUserVendors as IEditableDropdown[]
                                                  )?.find(
                                                    info => +info?.value === +userRole?.vendorId
                                                  )?.userManagerCanEdit === false
                                                )}
                                                onChange={e =>
                                                  setFieldValue(
                                                    `vendorUserRoles.${index}.vendorId`,
                                                    e.target.value
                                                  )
                                                }
                                                SelectProps={{
                                                  MenuProps: {
                                                    sx: {
                                                      maxHeight: '30rem'
                                                    }
                                                  }
                                                }}
                                              >
                                                {selectableUserVendors?.map(
                                                  (vendor, vendorIndex) => {
                                                    return (
                                                      <MenuItem
                                                        disabled={
                                                          isDisabled(
                                                            (
                                                              selectableUserVendors as IEditableDropdown[]
                                                            )?.find(
                                                              role =>
                                                                +role?.value === +userRole?.vendorId
                                                            )?.userManagerCanEdit === false
                                                          ) ||
                                                          values?.vendorUserRoles
                                                            ?.map?.(role => +role.vendorId)
                                                            ?.includes(+vendor?.value)
                                                        }
                                                        key={`${vendorIndex}`}
                                                        value={vendor.value}
                                                      >
                                                        {vendor.description}
                                                      </MenuItem>
                                                    );
                                                  }
                                                )}
                                              </SMBSelect>
                                            </FormControl>
                                          </Grid>
                                          <Grid item xs={12} sm={5}>
                                            <SMBSelect
                                              variant="outlined"
                                              onChange={e => {
                                                setFieldValue(
                                                  `vendorUserRoles.${index}.roles`,
                                                  typeof e.target.value === 'string'
                                                    ? e.target.value.split(',')
                                                    : e.target.value
                                                );
                                              }}
                                              SelectProps={{
                                                multiple: true
                                              }}
                                              handleClear={() => {
                                                setFieldValue(`vendorUserRoles.${index}.roles`, []);
                                              }}
                                              required
                                              name={`roles`}
                                              id={`roles`}
                                              label="Roles"
                                              size="small"
                                              value={userRole.roles}
                                              error={userRole?.roles?.length === 0}
                                              helperText={
                                                userRole?.roles?.length === 0 ? 'Required' : ''
                                              }
                                              disabled={isDisabled(
                                                isFormDisabled ||
                                                  (
                                                    selectableUserVendors as IEditableDropdown[]
                                                  )?.find(
                                                    info => +info?.value === +userRole?.vendorId
                                                  )?.userManagerCanEdit === false
                                              )}
                                            >
                                              {userTypes.map((type: IUserType, index) => {
                                                return (
                                                  <MenuItem key={`${index + 1}`} value={type.value}>
                                                    {type.name}
                                                  </MenuItem>
                                                );
                                              })}
                                            </SMBSelect>
                                          </Grid>
                                          <Grid item xs={12} sm={2}>
                                            <Button
                                              id="remove-vendor"
                                              startIcon={<Close />}
                                              variant="text"
                                              disabled={
                                                (
                                                  selectableUserVendors as IEditableDropdown[]
                                                )?.find(
                                                  info => +info?.value === +userRole?.vendorId
                                                )?.userManagerCanEdit === false
                                              }
                                              onClick={() => {
                                                remove(index);
                                              }}
                                            >
                                              Remove
                                            </Button>
                                          </Grid>
                                        </Fragment>
                                      );
                                    })}
                                  </Grid>
                                  <Button
                                    id="add-vendor"
                                    color="primary"
                                    variant="text"
                                    startIcon={<Add />}
                                    disabled={
                                      values?.vendorUserRoles?.length >=
                                      Math.max(
                                        userManagementVendorOptions?.length ?? 0,
                                        editOptions?.length ?? 0
                                      )
                                    }
                                    onClick={() =>
                                      push({
                                        vendorId: '',
                                        roles: []
                                      })
                                    }
                                  >
                                    Add Vendor
                                  </Button>
                                </>
                              );
                            }}
                          </FieldArray>
                        </Grid>
                      </Grid>
                    </Card>
                    <Box marginTop="1rem" display="flex" alignItems="center">
                      <Button
                        disabled={!dirty || isSubmitting || !isValid}
                        type="submit"
                        startIcon={<Save />}
                        variant="contained"
                        color="primary"
                      >
                        Save
                      </Button>
                      <Button
                        type="button"
                        variant="contained"
                        color="inherit"
                        startIcon={<Close />}
                        onClick={() => {
                          handleClose();
                        }}
                      >
                        Cancel
                      </Button>
                    </Box>
                  </Form>
                </Box>
              </Fade>
            </Modal>
          );
        }}
      </Formik>
    </>
  );
};
